import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import {
  DEFAULT_ZOOM,
  ZOOM_STEP,
  buildContentTransformStyle,
  buildGridBackgroundStyle,
  clampZoom,
  computeFitTransform,
  computeZoomAroundCenter,
  isEditableTarget,
  shouldIgnoreCanvasPan,
  computePannedPosition,
  measureContentBounds,
  adjustPanForViewportWidthChange,
  processCanvasWheelEvent,
} from '../utils/workflowCanvasViewportUtils';

export function useWorkflowCanvasViewport() {
  const viewportRef = useRef(null);
  const contentRef = useRef(null);
  const panSessionRef = useRef(null);
  const [spacePanActive, setSpacePanActive] = useState(false);

  const [zoom, setZoom] = useState(DEFAULT_ZOOM);
  const [pan, setPan] = useState({ x: 0, y: 0 });
  const [panMode, setPanMode] = useState(false);
  const [isPanning, setIsPanning] = useState(false);
  const panRef = useRef(pan);
  const zoomRef = useRef(zoom);

  useEffect(() => {
    panRef.current = pan;
  }, [pan]);

  useEffect(() => {
    zoomRef.current = zoom;
  }, [zoom]);

  const getViewportSize = useCallback(() => {
    const el = viewportRef.current;
    if (!el) return { width: 0, height: 0 };
    return { width: el.clientWidth, height: el.clientHeight };
  }, []);

  const applyZoom = useCallback((nextZoom) => {
    const { width, height } = getViewportSize();
    setZoom((currentZoom) => {
      const next = clampZoom(nextZoom);
      if (width && height) {
        setPan((currentPan) => {
          const centered = computeZoomAroundCenter({
            zoom: currentZoom,
            nextZoom: next,
            pan: currentPan,
            viewportWidth: width,
            viewportHeight: height,
          });
          return centered.pan;
        });
      }
      return next;
    });
  }, [getViewportSize]);

  const zoomIn = useCallback(() => {
    setZoom((currentZoom) => {
      const next = clampZoom(currentZoom + ZOOM_STEP);
      const { width, height } = getViewportSize();
      if (width && height) {
        setPan((currentPan) => computeZoomAroundCenter({
          zoom: currentZoom,
          nextZoom: next,
          pan: currentPan,
          viewportWidth: width,
          viewportHeight: height,
        }).pan);
      }
      return next;
    });
  }, [getViewportSize]);

  const zoomOut = useCallback(() => {
    setZoom((currentZoom) => {
      const next = clampZoom(currentZoom - ZOOM_STEP);
      const { width, height } = getViewportSize();
      if (width && height) {
        setPan((currentPan) => computeZoomAroundCenter({
          zoom: currentZoom,
          nextZoom: next,
          pan: currentPan,
          viewportWidth: width,
          viewportHeight: height,
        }).pan);
      }
      return next;
    });
  }, [getViewportSize]);

  const resetView = useCallback(() => {
    const { width, height } = getViewportSize();
    const fit = computeFitTransform({
      bounds: measureContentBounds(contentRef.current),
      viewportWidth: width,
      viewportHeight: height,
    });
    setZoom(DEFAULT_ZOOM);
    setPan(fit.pan);
  }, [getViewportSize]);

  const fitToScreen = useCallback(() => {
    const { width, height } = getViewportSize();
    const bounds = measureContentBounds(contentRef.current);
    const fit = computeFitTransform({
      bounds,
      viewportWidth: width,
      viewportHeight: height,
    });
    setZoom(fit.zoom);
    setPan(fit.pan);
  }, [getViewportSize]);

  const startPan = useCallback((clientX, clientY) => {
    panSessionRef.current = {
      startX: clientX,
      startY: clientY,
      originPan: pan,
    };
    setIsPanning(true);
  }, [pan]);

  const movePan = useCallback((clientX, clientY) => {
    const session = panSessionRef.current;
    if (!session) return;
    setPan(computePannedPosition(
      session.originPan,
      session.startX,
      session.startY,
      clientX,
      clientY,
    ));
  }, []);

  const endPan = useCallback(() => {
    panSessionRef.current = null;
    setIsPanning(false);
  }, []);

  const togglePanMode = useCallback(() => {
    setPanMode((value) => !value);
  }, []);

  const effectivePanMode = panMode || spacePanActive;

  const handleViewportPointerDown = useCallback((event) => {
    if (event.button !== 0) return;
    if (isEditableTarget(event.target)) return;
    if (shouldIgnoreCanvasPan(event.target)) return;
    event.preventDefault();
    startPan(event.clientX, event.clientY);
  }, [startPan]);

  useEffect(() => {
    const onPointerMove = (event) => {
      if (!panSessionRef.current) return;
      movePan(event.clientX, event.clientY);
    };
    const onPointerUp = () => endPan();
    const onPointerCancel = () => endPan();
    const onBlur = () => endPan();

    window.addEventListener('pointermove', onPointerMove);
    window.addEventListener('pointerup', onPointerUp);
    window.addEventListener('pointercancel', onPointerCancel);
    window.addEventListener('blur', onBlur);
    return () => {
      window.removeEventListener('pointermove', onPointerMove);
      window.removeEventListener('pointerup', onPointerUp);
      window.removeEventListener('pointercancel', onPointerCancel);
      window.removeEventListener('blur', onBlur);
    };
  }, [endPan, movePan]);

  useEffect(() => {
    const viewport = viewportRef.current;
    if (!viewport || typeof ResizeObserver === 'undefined') return undefined;

    let previousWidth = viewport.clientWidth;
    const observer = new ResizeObserver((entries) => {
      const entry = entries[0];
      if (!entry) return;
      const nextWidth = entry.contentRect.width;
      const editorOpen = viewport.closest('[data-workflow-editor-open="true"]') != null;
      if (!previousWidth || Math.abs(previousWidth - nextWidth) < 1) {
        previousWidth = nextWidth;
        return;
      }
      if (editorOpen) {
        previousWidth = nextWidth;
        return;
      }
      setPan((currentPan) => adjustPanForViewportWidthChange(
        currentPan,
        previousWidth,
        nextWidth,
      ));
      previousWidth = nextWidth;
    });

    observer.observe(viewport);
    return () => observer.disconnect();
  }, []);

  useEffect(() => {
    const viewport = viewportRef.current;
    if (!viewport) return undefined;

    const onWheel = (event) => {
      const result = processCanvasWheelEvent(event, {
        zoom: zoomRef.current,
        pan: panRef.current,
        getViewportSize,
      });

      if (!result.handled) return;

      if (result.preventDefault) {
        event.preventDefault();
        event.stopPropagation();
      }

      setPan(result.pan);
      if (result.zoom !== zoomRef.current) {
        setZoom(result.zoom);
      }
    };

    viewport.addEventListener('wheel', onWheel, { passive: false });
    return () => viewport.removeEventListener('wheel', onWheel);
  }, [getViewportSize]);

  useEffect(() => {
    const onKeyDown = (event) => {
      if (isEditableTarget(event.target)) return;

      if (event.code === 'Space' && !spacePanActive) {
        setSpacePanActive(true);
        event.preventDefault();
      }
      if (event.key === 'Escape') {
        if (panMode) {
          setPanMode(false);
        }
        if (panSessionRef.current) {
          endPan();
        }
      }
      if ((event.metaKey || event.ctrlKey) && event.key === '0') {
        event.preventDefault();
        resetView();
      }
      if ((event.metaKey || event.ctrlKey) && (event.key === '=' || event.key === '+')) {
        event.preventDefault();
        zoomIn();
      }
      if ((event.metaKey || event.ctrlKey) && event.key === '-') {
        event.preventDefault();
        zoomOut();
      }
    };

    const onKeyUp = (event) => {
      if (event.code === 'Space') {
        setSpacePanActive(false);
        endPan();
      }
    };

    window.addEventListener('keydown', onKeyDown);
    window.addEventListener('keyup', onKeyUp);
    return () => {
      window.removeEventListener('keydown', onKeyDown);
      window.removeEventListener('keyup', onKeyUp);
    };
  }, [endPan, panMode, resetView, spacePanActive, zoomIn, zoomOut]);

  const viewportStyle = useMemo(() => ({
    cursor: isPanning ? 'grabbing' : 'grab',
  }), [isPanning]);

  const contentStyle = useMemo(() => buildContentTransformStyle(pan, zoom), [pan, zoom]);
  const gridStyle = useMemo(() => buildGridBackgroundStyle(pan), [pan]);

  return {
    viewportRef,
    contentRef,
    zoom,
    pan,
    isPanning,
    panMode,
    effectivePanMode,
    setZoom: applyZoom,
    zoomIn,
    zoomOut,
    resetView,
    fitToScreen,
    startPan,
    movePan,
    endPan,
    togglePanMode,
    setPanMode,
    viewportStyle,
    contentStyle,
    gridStyle,
    handleViewportPointerDown,
  };
}

export default useWorkflowCanvasViewport;
